import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import Button from '../components/button/Button';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Input from '../components/input/Input';
import Table from '../components/table/table';
import Icon from '../components/icon/Icon';
import Image from '../components/image/Image';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import DownloadAnnexModal from '../components/modals/DownloadAnnexModal';
import CasfogUploadModal from '../components/modals/CasfogUploadModal';
import { ITableData } from '../types/interfaces';

import { getAnnexes, AnnexItem } from '../services/AnnexesService';
import { routes } from '../routes/routePaths';

const Annexes = () => {
  const navigate = useNavigate();
  const name = localStorage.getItem('name');
  const [annexes, setAnnexes] = useState<AnnexItem[]>([]);
  const [tableData, setTableData] = useState<ITableData>();
  const [page, setPage] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [debouncedSearchText] = useDebounce(searchText, 300);

  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  const [selectedAnnex, setSelectedAnnex] = useState<AnnexItem | null>(null);

  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [uploadAnnex, setUploadAnnex] = useState<AnnexItem | null>(null);

  const lastRequest = useRef(0);

  useEffect(() => {
    setIsLoading(true);
    const requestId = Date.now();
    lastRequest.current = requestId;

    getAnnexes(page, 10, debouncedSearchText)
      .then((response: any) => {
        if (lastRequest.current !== requestId) return;

        if (response && response.success && response.data && response.data.content) {
          setTableData({
            size: response.data.size,
            page: response.data.page,
            totalElements: response.data.total_elements,
            totalPages: response.data.total_pages,
          });

          setAnnexes(response.data.content);
        } else {
          setAnnexes([]);
        }

        setIsLoading(false);
        setIsSearching(false);
      })
      .catch(() => {
        setAnnexes([]);
        setIsLoading(false);
        setIsSearching(false);
      });
  }, [page, debouncedSearchText]);

  const handleChangePage = (data: number) => {
    if (data < 0 || data === tableData?.totalPages) {
      return;
    }
    setPage(data);
  };

  const handleSearch = () => {
    setIsSearching(true);
    setPage(0);
  };

  const handleRunAnnex = (annexItem: AnnexItem) => {
    setSelectedAnnex(annexItem);
    setShowDownloadModal(true);
  };

  const handleCloseDownloadModal = () => {
    setShowDownloadModal(false);
    setSelectedAnnex(null);
  };

  const handleUploadToCasfog = (annexItem: AnnexItem) => {
    setUploadAnnex(annexItem);
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setUploadAnnex(null);
  };

  const handleViewHistory = () => {
    navigate(routes.casfogHistory);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      if (dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          const day = parts[0];
          const month = parts[1];
          const year = parts[2];
          const formattedDate = `${month}/${day}/${year}`;
          const date = new Date(formattedDate);

          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('es-ES');
          }
        }
      }

      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('es-ES');
      }

      return dateString;
    } catch {
      return dateString;
    }
  };

  const columns = [
    {
      name: 'Nombre',
      selector: (item: AnnexItem) => (
        <P type='2' variant='neutral' withMargin={false}>
          {item.name}
        </P>
      ),
    },
    {
      name: 'Última ejecución',
      selector: (item: AnnexItem) => (
        <P type='2' variant='neutral' withMargin={false}>
          {formatDate(item.updated_at || item.created_at)}
        </P>
      ),
    },
    {
      name: 'Acciones',
      selector: (item: AnnexItem) => (
        <Card display='flex' gap='16px' justifyContent='center' alignItems='center'>
          <button
            type='button'
            title='Descargar'
            aria-label='Descargar'
            onClick={() => handleRunAnnex(item)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Icon name='download_outline' width={20} height={20} cursor='pointer' variant='info' />
          </button>
          {item.casfog_enabled && (
            <button
              type='button'
              title='CASFOG'
              aria-label='CASFOG'
              onClick={() => handleUploadToCasfog(item)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Icon name='send_outline' width={20} height={20} cursor='pointer' variant='success' />
            </button>
          )}
        </Card>
      ),
      center: true,
    },
  ];

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between' alignItems='center'>
        <Card>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Anexos
          </H>
          <P type='2' variant='neutral' withMargin={false}>
            Hola, {name}
          </P>
        </Card>
        <Button variant='secondary' onClick={handleViewHistory}>
          Historial CASFOG
        </Button>
      </Card>

      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ top: '4' }}>
        <Card width='400px' display='flex' gap='16px'>
          <Input
            type='text'
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            placeholder='Buscar por nombre'
            onChange={(e) => setSearchText(e.target.value)}
            onKeyup={() => handleSearch()}
          />
        </Card>
      </Card>

      {isSearching || isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : annexes.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={annexes}
            tableData={tableData}
            handleChangePage={(e: any) => handleChangePage(e)}
          />
        </Card>
      ) : searchText !== '' ? (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No encontramos resultados.
          </P>
          <P variant='primary' align='center'>
            Podés hacer una nueva búsqueda.
          </P>
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay anexos disponibles.
          </P>
        </Card>
      )}

      {selectedAnnex && selectedAnnex.id && (
        <DownloadAnnexModal
          isOpen={showDownloadModal}
          onClose={handleCloseDownloadModal}
          annexId={selectedAnnex.id}
          annexName={selectedAnnex.name}
          requiresYear={selectedAnnex.requires_year}
          requiresMonth={selectedAnnex.requires_month}
        />
      )}

      {uploadAnnex && uploadAnnex.id && (
        <CasfogUploadModal
          isOpen={showUploadModal}
          onClose={handleCloseUploadModal}
          annexId={uploadAnnex.id}
          annexName={uploadAnnex.name}
          annexCode={uploadAnnex.code}
        />
      )}
    </Card>
  );
};

export default Annexes;
